/**
 * @fileoverview Section "Tables" — Table brute + DataTable GNPC.
 *
 * Deux démos :
 *   1. `Table` shadcn brut — pour un usage simple sans logique.
 *   2. `DataTable` — patron standard toutes tables backoffice, avec tri,
 *      pagination cursor, filtres, empty state, loading state.
 *   3. `DataCard` — rendu mobile recommandé pour `DataTable.mobileCard`.
 *
 * La démo `DataTable` reproduit un fragment réaliste de liste de paiements
 * avec `StatusBadge` inline, téléphone masqué, timestamp FR,
 * montant XOF. Pas de fetch réel — données statiques pour l'illustration.
 */

import { useMemo, useState } from 'react';
import {
  Button,
  CopyButton,
  DataCard,
  DataTable,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@gnpc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { Filter, Search } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

// ============================================================
// Démo 1 — Table brute shadcn (rarement utilisée directement)
// ============================================================

interface RoleCapability {
  role: string;
  capability: string;
  description: string;
}

const ROLE_CAPABILITIES: readonly RoleCapability[] = [
  { role: 'AGENT', capability: 'dashboard:read', description: 'Voir dashboard temps réel' },
  { role: 'AGENT', capability: 'payment:read', description: 'Consulter les paiements' },
  {
    role: 'SUPERVISOR',
    capability: 'payment:replay',
    description: 'Rejouer un enregistrement STI',
  },
  {
    role: 'SUPERVISOR',
    capability: 'refund:request',
    description: 'Demander un remboursement',
  },
  { role: 'AUDITOR', capability: 'audit:read', description: 'Lire le journal d\'audit' },
  { role: 'AUDITOR', capability: 'audit:export', description: 'Exporter le journal d\'audit' },
  {
    role: 'ADMIN',
    capability: 'refund:approve',
    description: 'Approuver un remboursement (4-eyes)',
  },
  { role: 'ADMIN', capability: 'roles:manage', description: 'Gérer les rôles et permissions' },
];

// ============================================================
// Démo 2 — DataTable métier (extrait liste de paiements)
// ============================================================

type PaymentStatus = 'INITIATED' | 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
type SgiStatus = 'NOT_APPLICABLE' | 'PENDING' | 'PARTIAL' | 'RECORDED' | 'FAILED';

interface PaymentRow {
  id: string;
  merchantReference: string;
  userPhoneMasked: string;
  paymentStatus: PaymentStatus;
  sgiStatus: SgiStatus;
  totalAmount: number;
  createdAt: string;
}

const PAYMENTS: readonly PaymentRow[] = [
  {
    id: '0193a4c2-8d1c-7f5b-9c2e-6f8e1d2a4b5c',
    merchantReference: '01HXK9M0FZQR8Y3V6TZ2WC5B1Q',
    userPhoneMasked: '+225 07 XX XX XX 42',
    paymentStatus: 'PAID',
    sgiStatus: 'RECORDED',
    totalAmount: 45000,
    createdAt: '2026-07-14T09:23:18Z',
  },
  {
    id: '0193a4c2-8d1c-7f5b-9c2e-6f8e1d2a4b5d',
    merchantReference: '01HXK9M1AC0RS8Y3V6TZ2WC5B2R',
    userPhoneMasked: '+225 05 XX XX XX 07',
    paymentStatus: 'PAID',
    sgiStatus: 'FAILED',
    totalAmount: 12000,
    createdAt: '2026-07-14T09:19:41Z',
  },
  {
    id: '0193a4c2-8d1c-7f5b-9c2e-6f8e1d2a4b5e',
    merchantReference: '01HXK9M2Z8QRS8Y3V6TZ2WC5B3S',
    userPhoneMasked: '+225 01 XX XX XX 18',
    paymentStatus: 'PENDING',
    sgiStatus: 'NOT_APPLICABLE',
    totalAmount: 3200,
    createdAt: '2026-07-14T09:12:04Z',
  },
  {
    id: '0193a4c2-8d1c-7f5b-9c2e-6f8e1d2a4b5f',
    merchantReference: '01HXK9M3D3QRS8Y3V6TZ2WC5B4T',
    userPhoneMasked: '+225 07 XX XX XX 91',
    paymentStatus: 'FAILED',
    sgiStatus: 'NOT_APPLICABLE',
    totalAmount: 8500,
    createdAt: '2026-07-14T09:08:23Z',
  },
  {
    id: '0193a4c2-8d1c-7f5b-9c2e-6f8e1d2a4b60',
    merchantReference: '01HXK9M4X1QRS8Y3V6TZ2WC5B5U',
    userPhoneMasked: '+225 05 XX XX XX 33',
    paymentStatus: 'PAID',
    sgiStatus: 'PENDING',
    totalAmount: 60000,
    createdAt: '2026-07-14T08:57:11Z',
  },
];

/**
 * Formate un montant XOF en `12 345 F CFA` — sans décimales (XOF n'a pas
 * de sous-unité) avec séparateur d'espaces français.
 */
function formatXof(n: number): string {
  return `${n.toLocaleString('fr-FR')} F CFA`;
}

/**
 * Formate une date ISO en FR-CI (`14 juil. 2026 à 09:23`).
 */
function formatFr(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STATUS_COLORS: Record<PaymentStatus, string> = {
  INITIATED: 'bg-status-initiated/15 text-status-initiated',
  PENDING: 'bg-status-pending/15 text-status-pending',
  PAID: 'bg-status-paid/15 text-status-paid',
  FAILED: 'bg-status-failed/15 text-status-failed',
  EXPIRED: 'bg-status-expired/15 text-status-expired',
};

const SGI_COLORS: Record<SgiStatus, string> = {
  NOT_APPLICABLE: 'bg-status-not-applicable/30 text-quipux-navy-800',
  PENDING: 'bg-status-pending/15 text-status-pending',
  PARTIAL: 'bg-status-partial/15 text-status-partial',
  RECORDED: 'bg-status-recorded/15 text-status-recorded',
  FAILED: 'bg-status-failed/15 text-status-failed',
};

export function DsSectionTables(): JSX.Element {
  const [search, setSearch] = useState('');
  const [selectedReference, setSelectedReference] = useState(PAYMENTS[0]?.merchantReference ?? '');

  const filteredPayments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PAYMENTS.slice();
    return PAYMENTS.filter(
      (p) =>
        p.merchantReference.toLowerCase().includes(q) ||
        p.userPhoneMasked.toLowerCase().includes(q),
    );
  }, [search]);

  const columns: ColumnDef<PaymentRow>[] = useMemo(
    () => [
      {
        accessorKey: 'merchantReference',
        header: 'Référence',
        cell: ({ getValue }) => (
          <span className="inline-flex items-center gap-1 font-mono text-xs text-quipux-navy-950">
            {String(getValue()).slice(0, 12)}…
            <CopyButton
              value={String(getValue())}
              ariaLabel={`Copier ${String(getValue())}`}
            />
          </span>
        ),
      },
      {
        accessorKey: 'userPhoneMasked',
        header: 'Téléphone',
        cell: ({ getValue }) => (
          <span className="font-mono text-xs text-quipux-navy-900">{String(getValue())}</span>
        ),
      },
      {
        accessorKey: 'paymentStatus',
        header: 'Paiement',
        cell: ({ getValue }) => {
          const v = getValue() as PaymentStatus;
          return (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[v]}`}
            >
              {v}
            </span>
          );
        },
      },
      {
        accessorKey: 'sgiStatus',
        header: 'STI',
        cell: ({ getValue }) => {
          const v = getValue() as SgiStatus;
          return (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SGI_COLORS[v]}`}
            >
              {v}
            </span>
          );
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'Montant',
        cell: ({ getValue }) => (
          <span className="tabular-nums text-quipux-navy-950">{formatXof(Number(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Initié le',
        cell: ({ getValue }) => (
          <span className="text-xs text-muted-foreground">{formatFr(String(getValue()))}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-8">
      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Table brute</h3>
        <p className="text-xs text-muted-foreground">
          Rare — pour un tableau statique sans tri/pagination. Ex : matrice de rôles.
        </p>
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow className="hover:bg-transparent">
                <TableHead>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Rôle
                  </span>
                </TableHead>
                <TableHead>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Capacité
                  </span>
                </TableHead>
                <TableHead>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Description
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ROLE_CAPABILITIES.map((rc) => (
                <TableRow key={`${rc.role}-${rc.capability}`}>
                  <TableCell>
                    <span className="rounded-md bg-quipux-navy-100 px-2 py-0.5 font-mono text-xs text-quipux-navy-900">
                      {rc.role}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{rc.capability}</TableCell>
                  <TableCell className="text-sm text-quipux-navy-800">{rc.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">
          DataTable — extrait liste de paiements
        </h3>
        <p className="text-xs text-muted-foreground">
          Tri par colonne (cliquer sur l'en-tête), pagination cursor opaque, recherche sur
          référence / téléphone, empty state intégré et `rowAction` quand une ligne possède une
          seule action principale.
        </p>

        <DsPreview label='DataTable avec toolbar, tri, pagination cursor'>
          <div className="w-full">
            <DataTable
              columns={columns}
              data={filteredPayments}
              rowAction={{
                label: (payment) => `Ouvrir ${payment.merchantReference}`,
                onSelect: (payment) => setSelectedReference(payment.merchantReference),
              }}
              toolbar={
                <>
                  <Input
                    size="sm"
                    leftIcon={<Search />}
                    placeholder="Rechercher (référence, téléphone…)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button variant="outline" size="sm">
                    <Filter />
                    Filtres
                  </Button>
                </>
              }
              pagination={{
                onPreviousPage: () => undefined,
                onNextPage: () => undefined,
                hasPreviousPage: false,
                hasNextPage: true,
                label: `${filteredPayments.length} résultats`,
              }}
              emptyState={
                <p className="py-12 text-center text-sm text-muted-foreground">
                  Aucun paiement ne correspond à votre recherche.
                </p>
              }
              mobileCardLabel="Paiements de démonstration"
              mobileCard={(payment) => (
                <DataCard
                  eyebrow={payment.merchantReference}
                  title={payment.userPhoneMasked}
                  aside={
                    <span className="tabular-nums text-quipux-navy-950">
                      {formatXof(payment.totalAmount)}
                    </span>
                  }
                  meta={
                    <>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[payment.paymentStatus]}`}
                      >
                        {payment.paymentStatus}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${SGI_COLORS[payment.sgiStatus]}`}
                      >
                        {payment.sgiStatus}
                      </span>
                      <span className="text-muted-foreground">{formatFr(payment.createdAt)}</span>
                    </>
                  }
                />
              )}
            />
            <p className="text-muted-foreground mt-3 text-xs">
              Dernière ligne activée : <code>{selectedReference}</code>
            </p>
          </div>
        </DsPreview>

        <DsPreview label='État "loading" — 5 lignes squelette'>
          <div className="w-full">
            <DataTable columns={columns} data={[]} loading />
          </div>
        </DsPreview>

        <DsPreview label={`État "vide" — pas d'erreur, ton neutre`}>
          <div className="w-full">
            <DataTable
              columns={columns}
              data={[]}
              emptyState={
                <p className="py-12 text-center text-sm text-muted-foreground">
                  Aucun paiement enregistré pour cette période.
                </p>
              }
            />
          </div>
        </DsPreview>
      </div>
    </div>
  );
}
